import React from 'react';
import { isLotCancelled } from './LotUtil';

/**
 * Component to display the status of a closed lot
 * @param {Object} props
 * @param {Object} props.lot - The lot object
 * @param {Object} props.currentUser - Current logged in user
 * @param {number} props.auctionOwnerId - ID of the auction owner
 * @param {boolean} props.isLoading - Whether the lot status is being updated
 */
const ClosedLotStatus = ({ lot, currentUser, auctionOwnerId, isLoading }) => {
    console.log('[ClosedLotStatus] Rendering with status:', lot.status, 'for lot:', lot.id, 'isLoading:', isLoading);
    
    // Show loading state if the lot status is being updated
    if (isLoading) {
        const isAuctionOwner = currentUser?.id === auctionOwnerId;
        
        return (
            <div style={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(114, 46, 209, 0.95), rgba(24, 144, 255, 0.95))',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                zIndex: 10,
                backdropFilter: 'blur(3px)',
                animation: 'lotClosing 2s ease-in-out infinite'
            }} className="lot-status" data-tour="lot-status">
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    textAlign: 'center',
                    padding: '16px'
                }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        border: '3px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '3px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        marginBottom: '12px'
                    }} />
                    <div style={{ 
                        fontSize: '14px', 
                        fontWeight: 'bold',
                        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        marginBottom: '4px'
                    }}>
                        {isAuctionOwner ? '🎯 Finalizing Lot...' : '🎯 Finalizing Auction...'}
                    </div>
                    <div style={{ 
                        fontSize: '10px', 
                        opacity: 0.9,
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                    }}>
                        {isAuctionOwner ? 'Processing final bids' : 'Determining winner'}
                    </div>
                </div>
            </div>
        );
    }
    
    // If the lot is cancelled, show "Cancelled" regardless of other conditions
    if (lot.status === 'cancelled') {
        console.log('[ClosedLotStatus] Showing cancelled status for lot:', lot.id);
        return (
            <div style={{ 
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                padding: '4px',
                background: '#722ed1',
                color: 'white',
                textAlign: 'center',
                fontWeight: 'bold',
            }} className="lot-status" data-tour="lot-status">
                Cancelled
            </div>
        );
    }

    // If the lot is timed out, show "Timed Out"
    if (lot.status === 'timeout') {
        console.log('[ClosedLotStatus] Showing timeout status for lot:', lot.id);
        return (
            <div style={{ 
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                padding: '4px',
                background: '#fa8c16',
                color: 'white',
                textAlign: 'center',
                fontWeight: 'bold',
            }} className="lot-status" data-tour="lot-status">
                Timed Out
            </div>
        );
    }

    // Check if lot is effectively cancelled (closed without reaching reserve)
    const isCancelled = isLotCancelled(lot);
    
    // If cancelled (closed without reaching reserve), show purple background
    if (isCancelled) {
        console.log('[ClosedLotStatus] Showing cancelled status for closed lot without reserve:', lot.id);
        return (
            <div style={{ 
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                padding: '4px',
                background: '#722ed1',
                color: 'white',
                textAlign: 'center',
                fontWeight: 'bold',
            }} className="lot-status" data-tour="lot-status">
                Cancelled
            </div>
        );
    }

    // For closed lots that reached reserve, use the existing logic
    return (
        <div style={{ 
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '4px',
            background: currentUser?.id === auctionOwnerId 
                ? '#1890ff'
                : lot.last_bid_user_id 
                    ? (lot.last_bid_user_id === currentUser?.id 
                        ? '#52c41a'
                        : '#ff4d4f')
                        : '#faad14',
            color: 'white',
            textAlign: 'center',
            fontWeight: 'bold',
        }} className="lot-status">
            {currentUser?.id === auctionOwnerId ? 'Sold' : (
                lot.last_bid_user_id ? (
                    lot.last_bid_user_id === currentUser?.id 
                        ? 'You Won!' 
                        : 'Sold to Other'
                ) : 'Not Sold'
            )}
        </div>
    );
};

export default ClosedLotStatus;

